import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { Button, Col, Pagination, Row, Spin } from 'antd';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import ReactVisibilitySensor from 'react-visibility-sensor';

import AccountNav from '../../components/AccountNav/AccountNav';
import AccountNavMobile from '../../components/AccountNavMobile/AccountNavMobile';
import MarketFooter from '../../components/MarketFooter/MarketFooter';
import MarketSliderCard from '../../components/MarketSliderCard/MarketSliderCard';
import NotifyMeModal from '../../components/NotifyMeModal/NotifyMeModal';
import QuickBuyInitial from '../../components/QuickBuyInitialOffering/QuickBuyInitial';
import QuickBuyInitialDrawer from '../../components/QuickBuyInitialOffering/QuickBuyInitialDrawer';
import FavSearchFilter from '../../components/SearchFilter/FavSearchFilter';
import TradeDrawer from '../../pages/PropertyListing/TradeDrawer';
import TradeModal from '../../pages/PropertyListing/TradeModal';

import { CloseIcon } from '../../assets/icons';
import { Images } from '../../config/images';

import { useFavorite } from '../../Hooks/useFavorites';
import useMediaQuery from '../../Hooks/useMediaQuery';
import useQuickBuyOrder from '../../Hooks/useQuickBuyOrder';
import useQuickBuySecondary from '../../Hooks/useQuickBuySecondary';
import { addOrRemoveToFavouriteAction, fetchFavoriteAction, updateFavoriteData } from '../../store/reducer/favoriteSlice';
import { notifyMeForProperty } from '../../store/reducer/notifyMeSlice';
import { debounce } from '../../utils';
import { maxShareCalculator, maxShareCalculatorWithNoOfShare } from '../../utils/maxShareCalculator';
import { toastNotify } from '../../utils/toastNotify';
import { getUsCurrencyFormat } from '../../utils/usCurrencyFormatter';

const noFavoriteData = [
  <div className='no-data-found'>
    <div className='no-data-found-wrap'>
      <div className='mb-32 vector__icon'>
        <LazyLoadImage src={Images.nofavorite} alt='success vector art' />
      </div>
      <h4 className='title'>No favorites yet</h4>
      <p className='text-taupe'>Once you favorite a property, you’ll see it here.</p>
    </div>
  </div>,
];
const Favorites = () => {
  const [quickBuyPropertyData, setQuickBuyPropertyData] = useState({});
  const [openQuickBuy, setOpenQuickBuy] = useState('');
  const [isFetchingUserAccount, setIsFetchingUserAccount] = useState(false);
  const [openQuickBuySecondary, setOpenQuickBuySecondary] = useState('');
  const [notifyLoadingId, setNotifyLoadingId] = useState(0);
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [storeNotifyMeData, setStoreNotifyMeData] = useState({});
  const [newsLetterChecked, setNewsLetterChecked] = useState(false);
  const userData = useSelector((state) => state.userData);
  const filterRef = useRef(null);
  let navigate = useNavigate();
  const screenMD = useMediaQuery('(min-width: 1000px)');
  const favoriteState = useSelector((state) => state?.favorites);
  const quickByOptions = useQuickBuyOrder(setOpenQuickBuy, quickBuyPropertyData);
  const quickBuySecondaryOptions = useQuickBuySecondary(setOpenQuickBuySecondary, quickBuyPropertyData, {});
  const { data: { data: { propertyList = [], count = 0, nextPage } = {} } = {}, loading, isUpdating, currentPage } = favoriteState;

  const formRef = useRef();
  const dispatch = useDispatch();
  const { fetchFavorites } = useFavorite();
  useEffect(() => {
    if (!loading) {
      fetchFavorites();
    }
  }, []);

  const onBuyClick = async (data) => {
    if (Object.keys(userData?.userData).length > 0) {
      if (userData?.userData?.isKYCVerified) {
        try {
          setIsFetchingUserAccount(true);
          const OBJ = {
            userPurchasedShares: data?.userPurchasedShares,
            id: data?.propertyId,
            name: data?.propertyName,
            investors: data?.investors,
            sharesRemaining: data?.sharesRemaining,
            perSharePrice: data?.perSharePrice,
            minimum: data?.minimum,
            duration: data?.duration,
            percentageFunded: data?.percentageFunded,
            sharesPurchased: data?.sharesPurchased,
            totalShares: data?.totalShares,
            city: data?.cityName,
            state: data?.stateName,
            propertyType: data?.propertyType,
            images: data?.images,
            label: data?.label,
          };
          setQuickBuyPropertyData({ ...OBJ });
          setOpenQuickBuy('StepOne');
        } catch (error) {
          const { response } = error;
          toastNotify('error', response?.message || 'Something went wrong');
        } finally {
          setIsFetchingUserAccount(false);
        }
      } else {
        !userData?.userData?.isKYCVerified
          ? toastNotify('error', 'Please complete your kyc aml verification first.')
          : toastNotify('error', 'Your account is still in-review, you will be informed after completion of your account review.');
      }
    } else {
      navigate('/login');
    }
  };

  const handleOnFavoriteClick = (propertyId) => {
    dispatch(addOrRemoveToFavouriteAction({ propertyId, onFail: () => {}, onSuccess: onFavouriteSuccess }));
  };

  const onFavouriteSuccess = () => {
    if (count > 10) {
      onPaginationChange(1);
    } else {
      dispatch(
        updateFavoriteData({
          ...favoriteState?.data,
          data: {
            ...favoriteState?.data?.data,
            count: favoriteState?.data?.data?.count - 1,
          },
        })
      );
    }
  };

  const onSearchOrFilter = useCallback(
    debounce((data) => {
      dispatch(fetchFavoriteAction(data));
    }),
    []
  );

  const handleNotifyMe = async (value) => {
    setNotifyLoadingId(value?.id);
    await dispatch(
      notifyMeForProperty(
        Object.keys(userData?.userData).length
          ? {
              propertyId: value?.id,
              forSecondary: value?.isSecondary,
              onSuccess: () => {
                setOpen(false);
                setEmail('');
                setNotifyLoadingId(0);
                setNewsLetterChecked(false);
                formRef?.current?.resetFields();
              },
            }
          : {
              propertyId: storeNotifyMeData?.id,
              forSecondary: storeNotifyMeData?.isSecondary,
              signUpForNewsletter: newsLetterChecked,
              email: value.email,
              onSuccess: () => {
                setOpen(false);
                setEmail('');
                setNotifyLoadingId(0);
                setNewsLetterChecked(false);
                formRef?.current?.resetFields();
              },
            }
      )
    );
  };

  const onPaginationChange = (page, isSmallScreen) => {
    let data = {
      page,
      isSmallScreen,
    };
    let propertyTypes = [];
    const { owned, ...selectedTypes } = filterRef?.current?.selectedFilters;

    if (filterRef?.current?.searchQuery.trim()) {
      data['searchQuery'] = filterRef?.current?.searchQuery;
    }
    Object.keys(selectedTypes).forEach((typeId) => {
      if (selectedTypes?.[typeId]) {
        propertyTypes.push(typeId);
      }
    });
    if (propertyTypes.length) {
      data['propertyTypes'] = propertyTypes;
    }
    if (owned) {
      data['owned'] = 1;
    }
    dispatch(fetchFavoriteAction(data));
  };
  return (
    <>
      {screenMD ? (
        <>
          <QuickBuyInitial
            quickBuyPropertyData={{ ...quickBuyPropertyData }}
            open={openQuickBuy}
            modalState={setOpenQuickBuy}
            quickByOptions={quickByOptions}
            featchData={() => onPaginationChange(currentPage, false)}
          />
          <TradeModal
            quickBuySecondaryOptions={quickBuySecondaryOptions}
            open={openQuickBuySecondary}
            modalState={setOpenQuickBuySecondary}
            quickBuyPropertyData={quickBuyPropertyData}
            isSingleTab={true}
            isSell={'1'}
            featchData={() => {
              onPaginationChange(currentPage, false);
              setOpenQuickBuySecondary();
            }}
          />
        </>
      ) : (
        <>
          <QuickBuyInitialDrawer
            quickBuyPropertyData={{ ...quickBuyPropertyData }}
            open={openQuickBuy}
            modalState={setOpenQuickBuy}
            quickByOptions={quickByOptions}
            featchData={() => onPaginationChange(currentPage, false)}
          />
          <TradeDrawer
            quickBuySecondaryOptions={quickBuySecondaryOptions}
            open={openQuickBuySecondary}
            modalState={setOpenQuickBuySecondary}
            quickBuyPropertyData={quickBuyPropertyData}
            isSingleTab={true}
            isSell={'1'}
            featchData={() => {
              onPaginationChange(currentPage, false);
              setOpenQuickBuySecondary();
            }}
          />
        </>
      )}
      <AccountNav />
      <AccountNavMobile />
      <Spin spinning={loading || isUpdating}>
        <div className='marketplace marketplaceinitial'>
          <div className='portfolio__head'>
            <div className='container-fluid'>
              <h3 className='mb-0'>Favorites</h3>
            </div>
          </div>
          {Number(count) > 0 ||
          filterRef?.current?.searchQuery?.trim() ||
          Object?.values(filterRef?.current?.selectedFilters || {}).some((x) => x === true) ? (
            <FavSearchFilter page='Favorites' totalProperties={Number(count) || 0} onSearchOrFilter={onSearchOrFilter} ref={filterRef} />
          ) : (
            ''
          )}
          {Number(count) > 0 ? (
            <div className='initial-offer-wrapper'>
              <div className='container-fluid'>
                <Row gutter={[32, 32]}>
                  {React.Children.toArray(
                    propertyList?.map((item) => {
                      return (
                        <Col xs={24} sm={12} md={8}>
                          {'bids' in item ? (
                            <MarketSliderCard
                              id={`${item.propertyId}`}
                              onFavoriteClick={() => {
                                handleOnFavoriteClick(item.propertyId);
                              }}
                              // isFavUpdating={count <= 10 && isUpdating && updatingPropertyId?.toString() === item?.propertyId?.toString()}
                              isFavorite={true}
                              isFetchingUserAccount={isFetchingUserAccount}
                              multiFamilyTotalUnit={item?.multiFamilyTotalUnit}
                              isSecondary={'bids' in item}
                              label={item?.label}
                              MarketBlockHead={item?.propertyName}
                              images={item?.images && Object.values(item?.images)}
                              Bids={item?.bids}
                              Asks={item?.asks}
                              LastPriceLabel='Last price'
                              LastPrice={`$ ${getUsCurrencyFormat(Number(item?.lastTradedPrice) || 0)}`}
                              AllTimeLabel='All time'
                              AllTime={`${getUsCurrencyFormat(Number(item?.allTime) || 0)}%`}
                              ShareDivLable='Div yield'
                              ShareDivValue={`${getUsCurrencyFormat(Number(item?.dividendYield))}%`}
                              ShareDivTooltip='The dividend percentage is the most recent profit-sharing amount paid by Realbricks to this property’s investors. It is paid, and varies, on a
                        quarterly basis. The dividend percentage is calculated by dividing the dividend payment by the raise amount. The dividend payment you receive
                        will be proportional to your ownership of the property.'
                              NotAvailableTooltip='The Activity shows the number of bids and ask done for the property. Currently there is no data available for bids and asks.'
                              PrimaryButtonBuy={(item?.label === 'New' || item?.label === 'Trending' || !item?.label) && 'Buy now'}
                              GhostNotify={item?.label === 'Coming Soon' && 'Notify me'}
                              LocationProperty={`${item?.cityName}, ${item?.stateName}`}
                              RentFamily={item?.propertyType}
                              notifyLoadingId={notifyLoadingId}
                              isBuyDisabled={
                                item?.ownedEquity >= 9.8 || maxShareCalculator(item?.totalShares, item?.requestedShares, item?.ownedEquity) < 1
                              }
                              notifyMeBtnClick={(value) => {
                                Object.keys(userData?.userData).length ? handleNotifyMe(value) : setStoreNotifyMeData(value) || setOpen(true);
                              }}
                              BtnClick={() => {
                                setQuickBuyPropertyData(item);
                                setOpenQuickBuySecondary('StepOne');
                              }}
                              propertyType={item?.propertyType}
                              propertyTypeImage={item?.propertyTypeImage}
                            />
                          ) : (
                            <MarketSliderCard
                              isFetchingUserAccount={isFetchingUserAccount}
                              onFavoriteClick={() => {
                                handleOnFavoriteClick(item.propertyId);
                              }}
                              // isFavUpdating={count <= 10 && isUpdating && updatingPropertyId?.toString() === item?.propertyId?.toString()}
                              id={`${item.propertyId}`}
                              label={item?.label}
                              isFavorite={true}
                              isSecondary={false}
                              MarketBlockHead={item.propertyName}
                              images={item?.images && Object.values(item?.images)}
                              RemainShareLable='Shares remaining'
                              RemainShares={item?.sharesRemaining}
                              ShareInvestorLabel='Investors'
                              ShareInvestor={item?.investors}
                              PrimaryButtonBuy={(item?.label === 'New' || item?.label === 'Trending' || !item?.label) && 'Buy now'}
                              GhostNotify={(item?.label === 'Coming Soon' || item?.label === 'Fully Funded') && 'Notify me'}
                              ProgressPercent={item?.percentageFunded?.toString()}
                              LocationProperty={`${item?.cityName}, ${item?.stateName}`}
                              RentFamily={item?.propertyType}
                              MinimumnSharesLabel='Share price'
                              MinimumnShares={`${'$' + item?.perSharePrice}`}
                              isBuyDisabled={
                                item?.percentageFunded >= 100 || maxShareCalculatorWithNoOfShare(item?.totalShares, 0, item?.userPurchasedShares) < 1
                              }
                              BtnClick={() => {
                                onBuyClick(item);
                              }}
                              notifyLoadingId={notifyLoadingId}
                              notifyMeBtnClick={(value) => {
                                Object.keys(userData?.userData).length ? handleNotifyMe(value) : setStoreNotifyMeData(value) || setOpen(true);
                              }}
                              propertyType={item?.propertyType}
                              propertyTypeImage={item?.propertyTypeImage}
                            />
                          )}
                        </Col>
                      );
                    }) || []
                  )}
                </Row>
                {count > 10 &&
                  (screenMD ? (
                    <div className='custom-pagination-wrap'>
                      <Pagination
                        defaultCurrent={currentPage}
                        current={currentPage}
                        total={count}
                        showSizeChanger={false}
                        onChange={(page) => onPaginationChange(page, false)}
                      />
                    </div>
                  ) : (
                    <ReactVisibilitySensor
                      onChange={(value) => {
                        // setmobilePage((prevItem) => prevItem + 1);
                        if (value && nextPage && propertyList?.length !== count) {
                          onPaginationChange(nextPage, true);
                        }
                        // handleScrolltoBottom(
                        //   value,
                        //   mobilePage,
                        //   propertyListMobileData,
                        //   selectedLocation,
                        //   selectedPropertyType,
                        //   selectedLeverages,
                        //   savedProperty,
                        //   ownProperty,
                        //   dividendPaidProperty,
                        //   priceRange,
                        //   sortType,
                        //   marketPlaceSearch,
                        //   true
                        // );
                      }}
                    >
                      <div className='agreement__modal__dot'>.</div>
                    </ReactVisibilitySensor>
                  ))}
              </div>
            </div>
          ) : filterRef?.current?.searchQuery?.trim() || Object?.values(filterRef?.current?.selectedFilters || {}).some((x) => x === true) ? (
            <NoSearchResult
              title='No properties match your filter or search criteria.'
              description='Try adjusting your filters or expanding your search to see more results.'
              filterText='Try removing some other filters to see more results.'
              handleRemoveTypeFilter={filterRef?.current?.handleOnRemovePropertyTypeFilter}
              handleClearAll={filterRef?.current?.handleOnRemoveAllFilter}
            ></NoSearchResult>
          ) : (
            noFavoriteData
          )}
          {/* No Search Result and No Favorite data */}
          {/* <NoSearchResult title="No properties match your filter or search criteria." description="Try adjusting your filters or expanding your search to see more results." filterText="Try removing some other filters to see more results."></NoSearchResult>
                {noFavoriteData} */}
        </div>
      </Spin>
      <MarketFooter />
      <NotifyMeModal
        open={open}
        setOpen={setOpen}
        email={email}
        setEmail={setEmail}
        newsLetterChecked={newsLetterChecked}
        setNewsLetterChecked={setNewsLetterChecked}
        handleNotifyMe={handleNotifyMe}
        ref={formRef}
      />
    </>
  );
};

function NoSearchResult(props) {
  const { title, description, filterText, handleRemoveTypeFilter, handleClearAll } = props;
  return (
    <div className='no-data-found'>
      <div className='no-data-found-wrap'>
        <div className='mb-32 vector__icon'>
          <LazyLoadImage src={Images.home} alt='success vector art' />
        </div>
        <h4 className='title'>{title}</h4>
        <p className='text-taupe'>{description}</p>
        <hr className='mt-40 mb-40' />
        <p className='mb-16 text-taupe'>{filterText}</p>
        <div className='remove__filters__button' onClick={handleRemoveTypeFilter}>
          <CloseIcon size={24} color='#4F3D38' />
          <span className='fz-16 text-taupe fw-600'>Remove Property type filters</span>
        </div>
        <Button onClick={handleClearAll} type='link'>
          Clear all
        </Button>
      </div>
    </div>
  );
}
export default Favorites;
